import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

// Default query options
const defaultQueryOptions = {
  queries: {
    retry: (failureCount: number, error: any) => {
      // Don't retry for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  },
  mutations: {
    retry: false,
  },
};

// Query cache for global error handling
const queryCache = new QueryCache({
  onError: (error: any, query) => {
    // Don't show toast for background refetches
    if (query.state.data !== undefined) {
      return;
    }

    const errorMessage = error?.message || 'An unexpected error occurred';
    
    // Don't show auth errors as toast (handled by interceptors)
    if (error?.status === 401) {
      return;
    }

    toast.error(errorMessage);
    console.error('Query Error:', error);
  },
});

// Mutation cache for global mutation error handling
const mutationCache = new MutationCache({
  onError: (error: any, variables, context, mutation) => {
    const errorMessage = error?.message || 'An unexpected error occurred';
    
    // Don't show auth errors as toast
    if (error?.status === 401) {
      return;
    }

    // Only show toast if mutation doesn't have custom error handling
    if (!mutation.options.onError) {
      toast.error(errorMessage);
    }

    console.error('Mutation Error:', error);
  },
  onSuccess: (data, variables, context, mutation) => {
    // Show success toast for mutations if configured
    if (mutation.options.meta?.successMessage) {
      toast.success(mutation.options.meta.successMessage as any);
    }
  },
});

// Create the query client
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: defaultQueryOptions,
});

// Query key factories for consistent key management
export const queryKeys = {
  // Auth
  auth: {
    profile: () => ['auth', 'profile'] as const,
    validate: () => ['auth', 'validate'] as const,
  },

  // Users
  users: {
    all: () => ['users'] as const,
    lists: () => [...queryKeys.users.all(), 'list'] as const,
    list: (params?: any) => [...queryKeys.users.lists(), params] as const,
    details: () => [...queryKeys.users.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    profile: () => [...queryKeys.users.all(), 'profile'] as const,
  },

  // Restaurants
  restaurants: {
    all: () => ['restaurants'] as const,
    lists: () => [...queryKeys.restaurants.all(), 'list'] as const,
    list: (params?: any) => [...queryKeys.restaurants.lists(), params] as const,
    details: () => [...queryKeys.restaurants.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.restaurants.details(), id] as const,
    stats: (id: string, period?: string) => [...queryKeys.restaurants.detail(id), 'stats', period] as const,
    status: (slug: string) => [...queryKeys.restaurants.all(), 'status', slug] as const,
  },

  // Menu
  menu: {
    all: () => ['menu'] as const,
    categories: {
      all: () => [...queryKeys.menu.all(), 'categories'] as const,
      lists: () => [...queryKeys.menu.categories.all(), 'list'] as const,
      list: (includeItems?: boolean) => [...queryKeys.menu.categories.lists(), includeItems] as const,
      details: () => [...queryKeys.menu.categories.all(), 'detail'] as const,
      detail: (id: string) => [...queryKeys.menu.categories.details(), id] as const,
    },
    items: {
      all: () => [...queryKeys.menu.all(), 'items'] as const,
      lists: () => [...queryKeys.menu.items.all(), 'list'] as const,
      list: (params?: any) => [...queryKeys.menu.items.lists(), params] as const,
      details: () => [...queryKeys.menu.items.all(), 'detail'] as const,
      detail: (id: string) => [...queryKeys.menu.items.details(), id] as const,
      stats: () => [...queryKeys.menu.items.all(), 'stats'] as const,
    },
    public: (restaurantSlug: string) => [...queryKeys.menu.all(), 'public', restaurantSlug] as const,
  },

  // Tables
  tables: {
    all: () => ['tables'] as const,
    lists: () => [...queryKeys.tables.all(), 'list'] as const,
    list: (params?: any) => [...queryKeys.tables.lists(), params] as const,
    details: () => [...queryKeys.tables.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.tables.details(), id] as const,
    availability: (params: any) => [...queryKeys.tables.all(), 'availability', params] as const,
    stats: (period?: string) => [...queryKeys.tables.all(), 'stats', period] as const,
  },

  // Orders
  orders: {
    all: () => ['orders'] as const,
    lists: () => [...queryKeys.orders.all(), 'list'] as const,
    list: (params?: any) => [...queryKeys.orders.lists(), params] as const,
    details: () => [...queryKeys.orders.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.orders.details(), id] as const,
    kitchen: (status?: string) => [...queryKeys.orders.all(), 'kitchen', status] as const,
    stats: (period?: string) => [...queryKeys.orders.all(), 'stats', period] as const,
  },
} as const;

// Helper functions for cache invalidation
export const invalidateQueries = {
  users: {
    all: () => queryClient.invalidateQueries({ queryKey: queryKeys.users.all() }),
    lists: () => queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() }),
    detail: (id: string) => queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(id) }),
  },
  
  restaurants: {
    all: () => queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.all() }),
    lists: () => queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.lists() }),
    detail: (id: string) => queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.detail(id) }),
  },
  
  menu: {
    all: () => queryClient.invalidateQueries({ queryKey: queryKeys.menu.all() }),
    categories: () => queryClient.invalidateQueries({ queryKey: queryKeys.menu.categories.all() }),
    items: () => queryClient.invalidateQueries({ queryKey: queryKeys.menu.items.all() }),
  },
  
  tables: {
    all: () => queryClient.invalidateQueries({ queryKey: queryKeys.tables.all() }),
    lists: () => queryClient.invalidateQueries({ queryKey: queryKeys.tables.lists() }),
    detail: (id: string) => queryClient.invalidateQueries({ queryKey: queryKeys.tables.detail(id) }),
  },
  
  orders: {
    all: () => queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() }),
    lists: () => queryClient.invalidateQueries({ queryKey: queryKeys.orders.lists() }),
    kitchen: () => queryClient.invalidateQueries({ queryKey: queryKeys.orders.kitchen() }),
    detail: (id: string) => queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id) }),
  },
};

// Optimistic update helpers
export const optimisticUpdates = {
  updateUser: (userId: string, updatedUser: any) => {
    queryClient.setQueryData(queryKeys.users.detail(userId), updatedUser);
    queryClient.setQueryData(queryKeys.users.lists(), (oldData: any) => {
      if (!oldData?.users) return oldData;
      return {
        ...oldData,
        users: oldData.users.map((user: any) => 
          user.id === userId ? { ...user, ...updatedUser } : user
        ),
      };
    });
  },

  updateTable: (tableId: string, updatedTable: any) => {
    queryClient.setQueryData(queryKeys.tables.detail(tableId), updatedTable);
    queryClient.setQueryData(queryKeys.tables.lists(), (oldData: any) => {
      if (!oldData?.tables) return oldData;
      return {
        ...oldData,
        tables: oldData.tables.map((table: any) => 
          table.id === tableId ? { ...table, ...updatedTable } : table
        ),
      };
    });
  },

  updateOrder: (orderId: string, updatedOrder: any) => {
    queryClient.setQueryData(queryKeys.orders.detail(orderId), updatedOrder);
    queryClient.setQueryData(queryKeys.orders.lists(), (oldData: any) => {
      if (!oldData?.orders) return oldData;
      return {
        ...oldData,
        orders: oldData.orders.map((order: any) => 
          order.id === orderId ? { ...order, ...updatedOrder } : order
        ),
      };
    });
  },
};

// Prefetch helpers
export const prefetchQueries = {
  userProfile: () => {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.users.profile(),
      queryFn: async () => {
        const { UserService } = await import('./api');
        return UserService.getProfile();
      },
    });
  },
  
  // menuCategories and tables prefetch disabled until API integration is complete
  // menuCategories: () => { ... },
  // tables: () => { ... },
};